import React, { useState, useCallback, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { SearchHeader } from "./components/SearchHeader";
import { SearchResults } from "./components/SearchResults";
import { LoadingState } from "./components/LoadingState";
import { SavedSearches } from "./SavedSearches";
import { useIsMobile } from "@/hooks/use-mobile";

export const LegalSearchInterface = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleSavedSearch = (event: any) => {
      const { content, title, sources } = event.detail;
      setSearchResults([{
        id: 'saved-search',
        title: 'Juridisk analyse',
        content: content,
        category: 'AI-analyse',
        source: 'Lovdata RAG',
        sources: sources
      }]);
      setSearchQuery(title);
    };

    window.addEventListener('showSavedSearch', handleSavedSearch);
    return () => window.removeEventListener('showSavedSearch', handleSavedSearch);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Søkefelt er tomt",
        description: "Vennligst skriv inn søkeord",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('rag-law-search', {
        body: { searchQuery: searchQuery.trim() }
      });

      if (error) throw error;

      if (data.response) {
        setSearchResults([{
          id: 'rag-response',
          title: 'Juridisk analyse',
          content: data.response,
          category: 'AI-analyse',
          source: 'Lovdata RAG',
          sources: data.sources
        }]);
        
        toast({
          title: isMobile ? "Søk fullført" : "Søk fullført",
          description: isMobile ? "" : "Analyserer relevante lover og forskrifter",
        });
      } else {
        toast({
          title: "Ingen resultater",
          description: isMobile ? "" : "Prøv å endre søkeord",
        });
      }
    } catch (error: any) {
      console.error('Error during search:', error);
      setError(isMobile ? "Søket feilet" : "Det oppstod en feil under søket. Vennligst prøv igjen senere.");
      toast({
        title: "Feil under søk",
        description: isMobile ? "Prøv igjen" : "Kunne ikke fullføre søket. Prøv igjen senere.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, toast, isMobile]);

  const handleSaveSearch = async (result: any) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast({
          title: "Ikke innlogget",
          description: isMobile ? "Logg inn først" : "Du må være innlogget for å lagre søk",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from('saved_law_searches').insert({
        user_id: userData.user.id,
        title: searchQuery,
        content: result.content,
        category: result.category,
        source: result.source,
        metadata: {
          sources: result.sources
        }
      });

      if (error) throw error;

      toast({
        title: "Søk lagret",
        description: isMobile ? "" : "Søket ditt er nå lagret og kan finnes i din profil",
      });
    } catch (error) {
      console.error('Error saving search:', error);
      toast({
        title: "Feil ved lagring",
        description: isMobile ? "Prøv igjen" : "Kunne ikke lagre søket. Prøv igjen senere.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        <SearchHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          isLoading={isLoading}
          error={error}
        />

        {isLoading && <LoadingState />}

        {searchResults && !isLoading && (
          <SearchResults 
            results={searchResults}
            onSaveSearch={handleSaveSearch}
          />
        )}

        <SavedSearches />
      </motion.div>
    </div>
  );
};